import { useProjectStore } from "../state/projectStore";
import { useEditorStore } from "../state/editorStore";
import { useEditor } from "../hooks/useEditor";
import { OPEN_MIDI, MAKER_FRETS, NOTE_NAMES, scalePitchClasses } from "../core/theory/chords";
import { userBeats } from "../core/notation/beatGrouping";
import { fxList } from "../types/domain";
import { useT } from "../i18n";

const STRINGS = [1, 2, 3, 4, 5, 6];          // high E (top) → low E (bottom)
const OPEN_LABEL = ["E", "B", "G", "D", "A", "E"];
const INLAY = new Set([3, 5, 7, 9, 12, 15, 17, 19, 21]);

// Touch-friendly, horizontally-scrollable fretboard. Tap a fret to add/replace/select a note on
// the active measure's selected beat. Scale tones are highlighted (root = cyan, scale = indigo).
export function Fretboard() {
  const analysis = useProjectStore((s) => s.analysis);
  const arrangement = useProjectStore((s) => s.arrangement);
  const activeMeasure = useEditorStore((s) => s.activeMeasure);
  const selectedNoteIdx = useEditorStore((s) => s.selectedNoteIdx);
  const { tapFret, tapDead } = useEditor();
  const t = useT();

  const scale = analysis ? scalePitchClasses(analysis.key) : new Set<number>();
  const tonicPc = analysis ? NOTE_NAMES.indexOf(analysis.key.tonic) : -1;

  // the (string,fret) pairs currently on the selected beat — highlighted on the board
  const active = new Set<string>();
  const deadStrings = new Set<number>();   // strings carrying a dead (✕) note on the selected beat
  const notes = arrangement.measures[activeMeasure]?.notes;
  if (notes && selectedNoteIdx >= 0) {
    const groups = userBeats(notes);
    const bi = groups.findIndex((g) => g.items.some((it) => it.idx === selectedNoteIdx));
    if (bi >= 0) groups[bi].items.forEach((it) => {
      if (it.n.rest) return;
      if (fxList(it.n.fx).includes("x")) deadStrings.add(it.n.string!);
      else active.add(`${it.n.string}:${it.n.fret}`);
    });
  }

  return (
    <div className="fretboard-scroll">
      <div className="fretboard" style={{ gridTemplateColumns: `34px repeat(${MAKER_FRETS + 1}, minmax(40px, 1fr)) 46px` }}>
        {STRINGS.map((s, si) => (
          <div className="fb-row" key={s} style={{ display: "contents" }}>
            <div className="fb-open">{OPEN_LABEL[si]}</div>
            {Array.from({ length: MAKER_FRETS + 1 }, (_, f) => {
              const pc = (OPEN_MIDI[s] + f) % 12;
              const inScale = scale.has(pc);
              const isRoot = pc === tonicPc;
              const isActive = active.has(`${s}:${f}`);
              const cls = "fb-cell" + (f === 0 ? " nut" : "") + (isActive ? " on" : "");
              return (
                <button key={f} className={cls} onClick={() => tapFret(s, f)}
                  title={t("fb.tip", { s, f, note: NOTE_NAMES[pc] })}>
                  {(inScale || isActive) && (
                    <span className={"fb-dot" + (isRoot ? " root" : "") + (isActive ? " act" : "")}>{f}</span>
                  )}
                </button>
              );
            })}
            {/* dead/mute (✕) at the right end of each string */}
            <button className={"fb-cell fb-x" + (deadStrings.has(s) ? " on" : "")}
              onClick={() => tapDead(s)} title={t("fb.dead", { s })}>✕</button>
          </div>
        ))}
        {/* fret-number footer */}
        <div className="fb-open" />
        {Array.from({ length: MAKER_FRETS + 1 }, (_, f) => (
          <div className={"fb-num" + (INLAY.has(f) ? " inlay" : "")} key={f}>{f}</div>
        ))}
        <div className="fb-num fb-x-num">✕</div>
      </div>
    </div>
  );
}
